import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosRequestConfig,
  InternalAxiosRequestConfig
} from 'axios';
import { Config } from '../config';
import { OAuthTokens } from './oauthService';

// Create axios instance with base URL from config
const apiClient: AxiosInstance = axios.create({
  baseURL: Config.apiUrl,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request Interceptor
 * - Attaches Authorization header if token is present in localStorage or sessionStorage
 * - Handles both traditional auth tokens and OAuth tokens
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Check for traditional auth token
    const authToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    
    // Check for OAuth tokens
    const oauthTokens = localStorage.getItem('oauth_tokens') || sessionStorage.getItem('oauth_tokens');
    
    if (authToken) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${authToken}`;
    } else if (oauthTokens) {
      try {
        const tokens: OAuthTokens = JSON.parse(oauthTokens);
        if (tokens.accessToken && tokens.expiresAt > Date.now()) {
          config.headers = config.headers || {};
          config.headers['Authorization'] = `Bearer ${tokens.accessToken}`;
        }
      } catch (error) {
        console.error('Invalid OAuth tokens:', error);
        // Clear invalid tokens
        localStorage.removeItem('oauth_tokens');
        sessionStorage.removeItem('oauth_tokens');
      }
    }
    
    // Add CSRF protection header
    config.headers = config.headers || {};
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response Interceptor
 * - Handles global response errors, e.g., unauthorized
 * - Clears both traditional and OAuth tokens on 401
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error('Unauthorized - clearing all tokens');
      // Clear all authentication tokens
      localStorage.removeItem('authToken');
      localStorage.removeItem('oauth_tokens');
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('oauth_tokens');
      // Optionally: redirect to login
      // window.location.href = '/login';
    } else if (error.response?.status === 403) {
      console.error('Forbidden - insufficient permissions');
    } else if (error.response?.status >= 500) {
      console.error('Server error:', error.response.status);
    }
    return Promise.reject(error);
  }
);

/**
 * API Service
 * - Generic HTTP method wrappers
 * - Example endpoint methods
 */
export const ApiService = {
  // Generic GET
  get: async <T = any>(url: string, config?: AxiosRequestConfig) => {
    const res = await apiClient.get<T>(url, config);
    return res.data;
  },

  // Generic POST
  post: async <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => {
    const res = await apiClient.post<T>(url, data, config);
    return res.data;
  },

  // Generic PUT
  put: async <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => {
    const res = await apiClient.put<T>(url, data, config);
    return res.data;
  },

  // Generic DELETE
  delete: async <T = any>(url: string, config?: AxiosRequestConfig) => {
    const res = await apiClient.delete<T>(url, config);
    return res.data;
  },

  // Traditional Auth Methods
  login: (email: string, password: string) => {
    return ApiService.post<{ arqiteToken?: string; user?: { username?: string } }>('/auth/login', { email, password });
  },

  signup: (email: string, password: string, organizationId?: string, username?: string) => {
    const payload = {
      email,
      password,
      organizationId,
      username,
    };
    return ApiService.post<{ message: string; user?: { username?: string } }>('/auth/register', payload);
  },

  // OAuth Methods
  oauthLogin: (provider: 'microsoft' | 'google', tokens: OAuthTokens, userInfo: any) => {
    return ApiService.post<{ token: string; user: any }>('/auth/oauth', {
      provider,
      accessToken: tokens.accessToken,
      idToken: tokens.idToken,
      userInfo
    });
  },

  refreshOAuthToken: (provider: 'microsoft' | 'google', refreshToken: string) => {
    return ApiService.post<OAuthTokens>('/auth/oauth/refresh', {
      provider,
      refreshToken
    });
  },

  // User Management
  getProfile: () => {
    return ApiService.get('/users/profile');
  },

  updateProfile: (data: any) => {
    return ApiService.put('/users/profile', data);
  },

  // Organization Management
  getOrganizations: () => {
    return ApiService.get('/orgs');
  },

  createOrganization: (data: { name: string; domain: string }) => {
    return ApiService.post('/orgs', data);
  },

  // Security Methods
  enable2FA: () => {
    return ApiService.post('/auth/2fa/enable');
  },

  verify2FA: (token: string) => {
    return ApiService.post('/auth/2fa/verify', { token });
  },

  disable2FA: (password: string) => {
    return ApiService.post('/auth/2fa/disable', { password });
  },

  // Password Management
  forgotPassword: (email: string) => {
    return ApiService.post('/auth/forgot-password', { email });
  },

  resetPassword: (token: string, newPassword: string) => {
    return ApiService.post('/auth/reset-password', { token, newPassword });
  },

  changePassword: (currentPassword: string, newPassword: string) => {
    return ApiService.post('/auth/change-password', { currentPassword, newPassword });
  },
};

export default ApiService;
